import { getAllPuzzleTypes } from "../PuzzleTypeConstants";
import { validateFilledCoordinatesCellConflicts, validateWordPositionValues } from "../filledCoordinatesValidation";

/**
 * Extracts hidden message from puzzle info (from scattered word in filledCoordinates or hiddenMessage field).
 * @param {Object} puzzleInfo - Puzzle info object
 * @returns {string|null} - Hidden message or null
 */
export function getHiddenMessageFromPuzzleInfo(puzzleInfo) {
    if (!puzzleInfo) return null;
    if (puzzleInfo?.filledCoordinates) {
        try {
            const filledCoordinates = JSON.parse(puzzleInfo.filledCoordinates);
            const scatteredWord = filledCoordinates.find((coord) => coord.direction === "scattered");
            return scatteredWord?.text || puzzleInfo?.hiddenMessage || null;
        } catch {
            return puzzleInfo?.hiddenMessage || null;
        }
    }
    return puzzleInfo?.hiddenMessage || null;
}

/**
 * Returns puzzle types filtered by allowedPuzzleTypes. When allowedPuzzleTypes is provided, only those types are returned.
 * @param {string[]|null} allowedPuzzleTypes - Array of puzzle type IDs to allow, or null for all
 * @returns {Object[]} - Filtered puzzle types
 */
export function getFilteredPuzzleTypes(allowedPuzzleTypes) {
    const allTypes = getAllPuzzleTypes();
    if (allowedPuzzleTypes && Array.isArray(allowedPuzzleTypes) && allowedPuzzleTypes.length > 0) {
        return allTypes.filter((puzzleType) => allowedPuzzleTypes.includes(puzzleType.id));
    }
    return allTypes;
}

/**
 * Parses and validates filled coordinates. Returns either { coords, filledCells } or { error }.
 * @param {Object} puzzleInfo - Puzzle info with filledCoordinates or filled_coordinates
 * @returns {{ coords: Array, filledCells: number }|{ error: Object }}
 */
export function parsePuzzleData(puzzleInfo) {
    const rawCoords = puzzleInfo?.filledCoordinates ?? puzzleInfo?.filled_coordinates;
    if (!rawCoords) {
        return { error: { valid: false, message: "No puzzle data" } };
    }

    try {
        const coords = typeof rawCoords === "string" ? JSON.parse(rawCoords) : (Array.isArray(rawCoords) ? rawCoords : []);
        const totalPositions = coords.reduce((total, word) => total + (word.positions ? word.positions.length : 0), 0);

        if (totalPositions === 0) {
            return { error: { valid: false, message: "No words placed" } };
        }

        const wordTexts = coords.map((word) => word.text?.toLowerCase()).filter((text) => text);
        const uniqueWords = new Set(wordTexts);
        const hasDuplicates = wordTexts.length !== uniqueWords.size;

        if (hasDuplicates) {
            const duplicates = wordTexts.filter((word, index) => wordTexts.indexOf(word) !== index);
            const uniqueDuplicates = [...new Set(duplicates)];
            const duplicateEntryIndices = new Set();
            const seenByWord = new Map();
            coords.forEach((entry, idx) => {
                const t = (entry.text || entry.word || "").trim().toLowerCase();
                if (!t) return;
                const count = (seenByWord.get(t) || 0) + 1;
                seenByWord.set(t, count);
                if (count > 1) duplicateEntryIndices.add(idx);
            });
            return {
                error: {
                    valid: false,
                    message: `Duplicate words found: ${uniqueDuplicates.join(", ")}`,
                    hasDuplicates: true,
                    duplicateEntryIndices: Array.from(duplicateEntryIndices)
                }
            };
        }

        const wordList = coords.map((w) => (w.text || "").trim()).filter(Boolean);
        const wordListLower = wordList.map((w) => w.toLowerCase());
        const containedPairs = [];
        for (let i = 0; i < wordListLower.length; i++) {
            for (let j = i + 1; j < wordListLower.length; j++) {
                const a = wordListLower[i];
                const b = wordListLower[j];
                if (a === b) continue;
                if (b.includes(a)) containedPairs.push({ short: wordList[i], long: wordList[j] });
                else if (a.includes(b)) containedPairs.push({ short: wordList[j], long: wordList[i] });
            }
        }
        if (containedPairs.length > 0) {
            const message =
                containedPairs.length === 1
                    ? `One word is fully covered by another: "${containedPairs[0].short}" is contained in "${containedPairs[0].long}". Remove or change one of them.`
                    : `One word is fully covered by another (${containedPairs.length} pair(s)): ${containedPairs.map((p) => `"${p.short}" in "${p.long}"`).join("; ")}. Remove or change one of them.`;
            return {
                error: {
                    valid: false,
                    message,
                    containedWords: containedPairs
                }
            };
        }

        const cellResult = validateFilledCoordinatesCellConflicts(coords);
        if (!cellResult.valid && cellResult.conflicts?.length > 0) {
            return {
                error: {
                    valid: false,
                    message: `Cell conflicts: same (row, col) with different values (${cellResult.conflicts.length} cell(s))`,
                    cellConflicts: cellResult.conflicts
                }
            };
        }

        const wordResult = validateWordPositionValues(coords);
        if (!wordResult.valid && wordResult.mismatches?.length > 0) {
            return {
                error: {
                    valid: false,
                    message: `Word/position value mismatch: positions do not spell the word (${wordResult.mismatches.length} word(s))`,
                    wordMismatches: wordResult.mismatches
                }
            };
        }

        const cellToEntryIndices = new Map();
        coords.forEach((entry, idx) => {
            const positions = entry.positions || [];
            positions.forEach((pos) => {
                if (pos.row != null && pos.col != null) {
                    const key = `${pos.row},${pos.col}`;
                    if (!cellToEntryIndices.has(key)) cellToEntryIndices.set(key, new Set());
                    cellToEntryIndices.get(key).add(idx);
                }
            });
        });
        const fullySharedEntryIndices = [];
        coords.forEach((entry, idx) => {
            const positions = entry.positions || [];
            if (positions.length === 0) return;
            const allShared = positions.every((pos) => {
                const key = `${pos.row},${pos.col}`;
                const indices = cellToEntryIndices.get(key);
                return indices && indices.size >= 2;
            });
            if (allShared) fullySharedEntryIndices.push(idx);
        });
        if (fullySharedEntryIndices.length > 0) {
            const words = fullySharedEntryIndices.map((i) => (coords[i].text || coords[i].word || "").trim()).filter(Boolean);
            const message =
                fullySharedEntryIndices.length === 1
                    ? `One word has no exclusive cells (all positions shared with other words): "${words[0]}". It can be removed.`
                    : `${fullySharedEntryIndices.length} word(s) have no exclusive cells (all positions shared with other words): ${words.join(", ")}. They can be removed.`;
            return {
                error: {
                    valid: false,
                    message,
                    fullySharedEntryIndices
                }
            };
        }

        const uniqueCells = new Set();
        coords.forEach((word) => {
            if (word.positions && Array.isArray(word.positions)) {
                word.positions.forEach((pos) => {
                    if (pos.row !== undefined && pos.col !== undefined) {
                        uniqueCells.add(`${pos.row},${pos.col}`);
                    }
                });
            }
        });
        const filledCells = uniqueCells.size;

        return { coords, filledCells };
    } catch {
        return { error: { valid: false, message: "Invalid puzzle data" } };
    }
}

const CLASSIC_GRID_CELLS = 144; // 12×12
const CROSSWORD_REQUIRED_CELLS = 123; // 85% of 144

/**
 * Board cell count from template dims. Classic / HIDDEN_MESSAGE = 144; Mini = rows×cols.
 */
export function resolveGridBoardCellCount(puzzleInfo) {
    if (!puzzleInfo) {
        return CLASSIC_GRID_CELLS;
    }
    if (puzzleInfo.type === "HIDDEN_MESSAGE") {
        return CLASSIC_GRID_CELLS;
    }
    const rows = Number(puzzleInfo.gridRows ?? puzzleInfo.grid_rows);
    const cols = Number(puzzleInfo.gridCols ?? puzzleInfo.grid_cols);
    if (Number.isFinite(rows) && rows > 0 && Number.isFinite(cols) && cols > 0) {
        return rows * cols;
    }
    return CLASSIC_GRID_CELLS;
}

/** WORD_SEARCH with a non-12×12 board (Mini family). */
export function isMiniWordSearchBoard(puzzleInfo) {
    if (!puzzleInfo || puzzleInfo.type === "HIDDEN_MESSAGE") {
        return false;
    }
    const rows = Number(puzzleInfo.gridRows ?? puzzleInfo.grid_rows);
    const cols = Number(puzzleInfo.gridCols ?? puzzleInfo.grid_cols);
    if (!Number.isFinite(rows) || !Number.isFinite(cols) || rows <= 0 || cols <= 0) {
        return false;
    }
    return !(rows === 12 && cols === 12);
}

/**
 * Get completion status for grid puzzles (WORD_SEARCH, HIDDEN_MESSAGE).
 * Uses rows×cols for Mini boards; Classic / HIDDEN_MESSAGE stay 12×12 (144).
 * @param {Object} puzzleInfo - Puzzle info
 * @returns {Object} - { valid, message, percentage?, ...errorFields }
 */
export function getGridPuzzleCompletionStatus(puzzleInfo) {
    const parsed = parsePuzzleData(puzzleInfo);
    if (parsed.error) return parsed.error;

    const { filledCells } = parsed;
    const totalCells = resolveGridBoardCellCount(puzzleInfo);
    const requiredCells = totalCells;
    const isComplete = filledCells >= requiredCells;
    const percentage = ((filledCells / totalCells) * 100).toFixed(1);
    const sizeHint = isMiniWordSearchBoard(puzzleInfo)
        ? ` (${Number(puzzleInfo.gridRows ?? puzzleInfo.grid_rows)}×${Number(puzzleInfo.gridCols ?? puzzleInfo.grid_cols)} Mini)`
        : "";

    if (isComplete) {
        const completionText =
            filledCells >= totalCells
                ? `${filledCells} cells filled (${percentage}% complete)${sizeHint}`
                : `all ${filledCells} cells filled (${percentage}% complete)${sizeHint}`;
        return {
            valid: true,
            message: `✓ No duplicates, ${completionText}`,
            percentage: parseFloat(percentage),
            totalCells,
        };
    }
    return {
        valid: false,
        message: `✓ No duplicates, ${filledCells}/${totalCells} cells filled (${percentage}% complete)${sizeHint} - Need ${requiredCells - filledCells} more cells`,
        percentage: parseFloat(percentage),
        totalCells,
    };
}

/**
 * Get completion status for crossword puzzles.
 * @param {Object} puzzleInfo - Puzzle info
 * @returns {Object} - { valid, message, percentage?, ...errorFields }
 */
export function getCrosswordCompletionStatus(puzzleInfo) {
    const parsed = parsePuzzleData(puzzleInfo);
    if (parsed.error) return parsed.error;

    const { filledCells } = parsed;
    const isComplete = filledCells >= CROSSWORD_REQUIRED_CELLS;
    const percentage = ((filledCells / CLASSIC_GRID_CELLS) * 100).toFixed(1);

    if (isComplete) {
        const completionText =
            filledCells < CLASSIC_GRID_CELLS
                ? `${filledCells} cells filled (${percentage}% complete - 85% threshold met)`
                : `${filledCells} cells filled (${percentage}% complete)`;
        return {
            valid: true,
            message: `✓ No duplicates, ${completionText}`,
            percentage: parseFloat(percentage)
        };
    }
    return {
        valid: false,
        message: `✓ No duplicates, ${filledCells}/${CLASSIC_GRID_CELLS} cells filled (${percentage}% complete) - Need ${CROSSWORD_REQUIRED_CELLS - filledCells} more cells`,
        percentage: parseFloat(percentage)
    };
}

/**
 * Returns words that use conflict cells.
 * @param {Object} puzzleInfo - Puzzle info
 * @param {Object} completionStatus - From getGridPuzzleCompletionStatus (must have cellConflicts)
 * @returns {{ coords: Array, wordsWithIndex: Array }}
 */
export function getWordsUsingConflictCells(puzzleInfo, completionStatus) {
    const rawCoords = puzzleInfo?.filledCoordinates ?? puzzleInfo?.filled_coordinates;
    if (!rawCoords) return { coords: [], wordsWithIndex: [] };
    const coords = typeof rawCoords === "string" ? JSON.parse(rawCoords) : Array.isArray(rawCoords) ? rawCoords : [];
    const cellResult = validateFilledCoordinatesCellConflicts(coords);
    if (cellResult.valid || !cellResult.conflicts?.length) return { coords, wordsWithIndex: [] };
    const conflictKeys = new Set(cellResult.conflicts.map((c) => `${c.row},${c.col}`));
    const wordsWithIndex = coords
        .map((entry, indexInCoords) => ({ entry, indexInCoords }))
        .filter(({ entry }) => {
            const positions = entry.positions || [];
            return positions.some((p) => p.row != null && p.col != null && conflictKeys.has(`${p.row},${p.col}`));
        });
    return { coords, wordsWithIndex };
}

/**
 * Returns words with position/value mismatches.
 */
export function getWordsForMismatches(puzzleInfo, completionStatus) {
    const rawCoords = puzzleInfo?.filledCoordinates ?? puzzleInfo?.filled_coordinates;
    const mismatches = completionStatus?.wordMismatches ?? [];
    if (!rawCoords || !Array.isArray(mismatches) || mismatches.length === 0) return { coords: [], wordsWithIndex: [] };
    const coords = typeof rawCoords === "string" ? JSON.parse(rawCoords) : Array.isArray(rawCoords) ? rawCoords : [];
    const mismatchActuals = new Set(mismatches.map((m) => String(m.actual || "")));
    const wordsWithIndex = coords
        .map((entry, indexInCoords) => {
            const positions = entry.positions || [];
            const actual = positions.map((p) => (p.value != null ? String(p.value) : "")).join(",");
            return { entry, indexInCoords, actual };
        })
        .filter(({ actual }) => mismatchActuals.has(actual))
        .map(({ entry, indexInCoords }) => ({ entry, indexInCoords }));
    return { coords, wordsWithIndex };
}

/**
 * Returns duplicate word entries (extras to remove).
 */
export function getWordsForDuplicates(puzzleInfo, completionStatus) {
    const rawCoords = puzzleInfo?.filledCoordinates ?? puzzleInfo?.filled_coordinates;
    const indices = completionStatus?.duplicateEntryIndices ?? [];
    if (!rawCoords || !Array.isArray(indices) || indices.length === 0) return { coords: [], wordsWithIndex: [] };
    const coords = typeof rawCoords === "string" ? JSON.parse(rawCoords) : Array.isArray(rawCoords) ? rawCoords : [];
    const indexSet = new Set(indices);
    const wordsWithIndex = coords
        .map((entry, indexInCoords) => ({ entry, indexInCoords }))
        .filter(({ indexInCoords }) => indexSet.has(indexInCoords));
    return { coords, wordsWithIndex };
}

/**
 * Returns contained (shorter) words from contained pairs.
 */
export function getWordsForContainedPairs(puzzleInfo, completionStatus) {
    const rawCoords = puzzleInfo?.filledCoordinates ?? puzzleInfo?.filled_coordinates;
    const pairs = completionStatus?.containedWords ?? [];
    if (!rawCoords || !Array.isArray(pairs) || pairs.length === 0) return { coords: [], wordsWithIndex: [] };
    const coords = typeof rawCoords === "string" ? JSON.parse(rawCoords) : Array.isArray(rawCoords) ? rawCoords : [];
    const shortWordsLower = new Set(pairs.map((p) => (p.short || "").toLowerCase().trim()).filter(Boolean));
    const wordsWithIndex = coords
        .map((entry, indexInCoords) => ({ entry, indexInCoords }))
        .filter(({ entry }) => {
            const t = (entry.text || entry.word || "").trim().toLowerCase();
            return t && shortWordsLower.has(t);
        });
    return { coords, wordsWithIndex };
}

/**
 * Returns fully shared words (no exclusive cells).
 */
export function getWordsForFullyShared(puzzleInfo, completionStatus) {
    const rawCoords = puzzleInfo?.filledCoordinates ?? puzzleInfo?.filled_coordinates;
    const indices = completionStatus?.fullySharedEntryIndices ?? [];
    if (!rawCoords || !Array.isArray(indices) || indices.length === 0) return { coords: [], wordsWithIndex: [] };
    const coords = typeof rawCoords === "string" ? JSON.parse(rawCoords) : Array.isArray(rawCoords) ? rawCoords : [];
    const indexSet = new Set(indices);
    const wordsWithIndex = coords
        .map((entry, indexInCoords) => ({ entry, indexInCoords }))
        .filter(({ indexInCoords }) => indexSet.has(indexInCoords));
    return { coords, wordsWithIndex };
}

function getWordsForBanned(puzzleInfo, bannedWordsCheckResult) {
    if (!puzzleInfo || !bannedWordsCheckResult?.bannedWords?.length) return { coords: [], wordsWithIndex: [] };
    const rawCoords = puzzleInfo?.filledCoordinates ?? puzzleInfo?.filled_coordinates;
    if (!rawCoords) return { coords: [], wordsWithIndex: [] };
    const coords = typeof rawCoords === "string" ? JSON.parse(rawCoords) : Array.isArray(rawCoords) ? rawCoords : [];
    const bannedSet = new Set(bannedWordsCheckResult.bannedWords.map((w) => (w || "").trim().toLowerCase()));
    const wordsWithIndex = coords
        .map((entry, indexInCoords) => ({ entry, indexInCoords }))
        .filter(({ entry }) => {
            const text = (entry.text || entry.word || "").trim().toLowerCase();
            return text && bannedSet.has(text);
        });
    return { coords, wordsWithIndex };
}

/**
 * Resolution type priority: cellConflicts > containedWords > duplicates > wordMismatches > fullyShared > bannedWords
 */
export function getWordsToResolve(puzzleInfo, completionStatus, bannedWordsCheckResult = null) {
    if (!puzzleInfo) return { coords: [], wordsWithIndex: [] };
    const hasCellConflicts = (completionStatus?.cellConflicts?.length ?? 0) > 0;
    const hasContainedWords = (completionStatus?.containedWords?.length ?? 0) > 0;
    const hasDuplicates = !!(completionStatus?.hasDuplicates && (completionStatus?.duplicateEntryIndices?.length ?? 0) > 0);
    const hasWordMismatches = (completionStatus?.wordMismatches?.length ?? 0) > 0;
    const hasFullySharedWords = (completionStatus?.fullySharedEntryIndices?.length ?? 0) > 0;
    const hasBannedWords = (bannedWordsCheckResult?.bannedCount ?? 0) > 0;

    if (completionStatus && hasCellConflicts) {
        const rawCoords = puzzleInfo?.filledCoordinates ?? puzzleInfo?.filled_coordinates;
        if (!rawCoords) return { coords: [], wordsWithIndex: [] };
        const coords = typeof rawCoords === "string" ? JSON.parse(rawCoords) : Array.isArray(rawCoords) ? rawCoords : [];
        const cellResult = validateFilledCoordinatesCellConflicts(coords);
        if (cellResult.valid || !cellResult.conflicts?.length) return { coords, wordsWithIndex: [] };
        const conflictKeys = new Set(cellResult.conflicts.map((c) => `${c.row},${c.col}`));
        const wordsWithIndex = coords
            .map((entry, indexInCoords) => ({ entry, indexInCoords }))
            .filter(({ entry }) => {
                const positions = entry.positions || [];
                return positions.some((p) => p.row != null && p.col != null && conflictKeys.has(`${p.row},${p.col}`));
            });
        return { coords, wordsWithIndex };
    }
    if (completionStatus && hasContainedWords) return getWordsForContainedPairs(puzzleInfo, completionStatus);
    if (completionStatus && hasDuplicates) return getWordsForDuplicates(puzzleInfo, completionStatus);
    if (completionStatus && hasWordMismatches) return getWordsForMismatches(puzzleInfo, completionStatus);
    if (completionStatus && hasFullySharedWords) return getWordsForFullyShared(puzzleInfo, completionStatus);
    if (hasBannedWords) return getWordsForBanned(puzzleInfo, bannedWordsCheckResult);
    return { coords: [], wordsWithIndex: [] };
}

/**
 * Whether the Confirm button should be disabled based on validation status.
 * @param {Object} status - Completion status from getGridPuzzleCompletionStatus or getCrosswordCompletionStatus
 * @param {boolean} containedWordsAcceptedChecked - User checked "contained words accepted"
 * @param {number} bannedCount - Number of banned words
 * @param {boolean} titleExists - Title already exists
 * @returns {boolean} - True if confirm should be disabled
 */
export function isConfirmDisabled(status, containedWordsAcceptedChecked, bannedCount = 0, titleExists = false) {
    if (bannedCount > 0 || titleExists) return true;
    if (!status) return true;
    const hasContainedWordsOnly =
        !status.valid &&
        (status.containedWords?.length ?? 0) > 0 &&
        (status.cellConflicts?.length ?? 0) === 0 &&
        (status.wordMismatches?.length ?? 0) === 0 &&
        (status.fullySharedEntryIndices?.length ?? 0) === 0;
    if (hasContainedWordsOnly && containedWordsAcceptedChecked) return false;
    return !status.valid;
}
